import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSignOutAlt } from 'react-icons/fa';
import './Sidebar.css';
import { logoutUser } from '../../api/services/authService';

const Sidebar = ({ items, activeItem, onItemClick, variant }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = () => {
    logoutUser();
    navigate('/login');
  };

  useEffect(() => {
    const handleToggle = (event) => {
      setIsOpen(event.detail);
    };

    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsOpen(false);
      }
    };

    window.addEventListener('toggleSidebar', handleToggle);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('toggleSidebar', handleToggle);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleItemClick = (itemId) => {
    onItemClick(itemId);
    if (window.innerWidth <= 768) {
      setIsOpen(false);
      window.dispatchEvent(new CustomEvent('toggleSidebar', { detail: false }));
    }
  };

  const renderLogoutButton = () => (
    <button type="button" className="nav-item logout-item" onClick={handleLogout}>
      <span className="nav-icon icon-logout">
        <FaSignOutAlt />
      </span>
      <span className="nav-label">Logout</span>
    </button>
  );

  const SECTION_ORDER = [
    'MANAGEMENT',
    'FINANCE',
    'TRUST & SECURITY',
    'COMMUNICATION',
    'SYSTEM',
    'OTHER',
  ];

  const sortSections = (sections) =>
    [...sections].sort((a, b) => {
      const indexA = SECTION_ORDER.indexOf(a);
      const indexB = SECTION_ORDER.indexOf(b);
      const orderA = indexA === -1 ? SECTION_ORDER.length : indexA;
      const orderB = indexB === -1 ? SECTION_ORDER.length : indexB;
      return orderA - orderB;
    });

  // Check if items are grouped by sections
  const hasSections = items.length > 0 && items[0].section;

  if (hasSections) {
    // Group items by section
    const groupedItems = items.reduce((acc, item) => {
      const section = item.section || 'OTHER';
      if (!acc[section]) {
        acc[section] = [];
      }
      acc[section].push(item);
      return acc;
    }, {});

    const orderedSections = sortSections(Object.keys(groupedItems));

    return (
      <>
        <aside className={`sidebar ${variant ? `sidebar-${variant}` : ''} ${isOpen ? 'open' : ''}`}>
          <nav className="sidebar-nav">
            {orderedSections.map((section) => (
              <div key={section} className="nav-section">
                <div className="nav-section-label">{section}</div>
                {groupedItems[section].map((item) => (
                  <button
                    key={item.id}
                    className={`nav-item ${activeItem === item.id ? 'active' : ''}`}
                    onClick={() => handleItemClick(item.id)}
                  >
                    <span className={`nav-icon ${typeof item.icon === 'string' ? 'nav-icon-emoji' : ''} ${activeItem === item.id ? 'active' : ''} icon-${item.id}`}>{item.icon}</span>
                    <span className="nav-label">{item.label}</span>
                    {item.badge && (
                      <span className="nav-badge">{item.badge}</span>
                    )}
                    {item.hasSubmenu && <span className="submenu-arrow">→</span>}
                  </button>
                ))}
              </div>
            ))}
            <div className="nav-section nav-section-logout">
              {renderLogoutButton()}
            </div>
          </nav>
        </aside>
        {isOpen && (
          <div
            className="sidebar-overlay"
            onClick={() => {
              setIsOpen(false);
              window.dispatchEvent(new CustomEvent('toggleSidebar', { detail: false }));
            }}
          />
        )}
      </>
    );
  }

  // Original flat structure
  return (
    <>
      <aside className={`sidebar ${variant ? `sidebar-${variant}` : ''} ${isOpen ? 'open' : ''}`}>
        <nav className="sidebar-nav">
          {items.map((item) => (
            <button
              key={item.id}
              className={`nav-item ${activeItem === item.id ? 'active' : ''}`}
              onClick={() => handleItemClick(item.id)}
            >
              <span className={`nav-icon ${typeof item.icon === 'string' ? 'nav-icon-emoji' : ''} icon-${item.id}`}>{item.icon}</span>
              <span className="nav-label">{item.label}</span>
              {item.badge && (
                <span className="nav-badge">{item.badge}</span>
              )}
              {item.hasSubmenu && <span className="submenu-arrow">→</span>}
            </button>
          ))}
          {renderLogoutButton()}
        </nav>
      </aside>
      {isOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => {
            setIsOpen(false);
            window.dispatchEvent(new CustomEvent('toggleSidebar', { detail: false }));
          }}
        />
      )}
    </>
  );
};

export default Sidebar;
